import { useFormikSubmit } from "@certn/api";
import { useClientPortalAPI } from "@certn/api/src/useClientPortalAPI";
import { showToast } from "@certn/cascadia";
import {
  Container,
  Divider,
  Link,
  LinkButton,
  ProgressLoader,
  Radio,
  RadioGroupFormikField,
  SectionMessage,
  SelectFormikField,
  SelectItem,
  Stack,
  Text,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Formik } from "formik";
import { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { string, object } from "yup";

import { UnifiedIntegrationContext } from "../../UnifiedIntegrationContext";
import { UnifiedIntegrationStepLayout } from "../UnifiedIntegrationStepLayout";

export function PackagesAndSettingsStep() {
  const { "*": integrationType } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { atsIntegrationData, unifiedConfig } = useContext(
    UnifiedIntegrationContext
  );

  const validationSchema = object().shape({
    default_package_id: string().required().label(t("Default package")),
    customer_group_id: string().required().label(t("Customer group")),
    trigger_stage: string().required().label(t("Trigger stage")),
  });

  const { data: triggerStages, isLoading: isLoadingTriggerStages } =
    useClientPortalAPI(
      "/api/internal/client-portal/ats-integrations/trigger-stages/",
      { connection_id: unifiedConfig?.connection_id }
    );

  const { data: packages, isLoading: isLoadingPackages } = useClientPortalAPI(
    "/api/internal/client-portal/ats-integrations/packages/"
  );
  const { data: customerGroups, isLoading: isLoadingCustomerGroups } =
    useClientPortalAPI(
      "/api/internal/client-portal/ats-integrations/customer-groups/"
    );

  const { submit } = useFormikSubmit(
    "/api/internal/client-portal/ats-integrations/create/",
    {
      onSuccess: () => {
        showToast(t("Integration set up successfully"), {
          type: "success",
        });
        navigate("/settings/ats-integrations");
      },
      showToastOnError: true,
    }
  );

  return (
    <Formik
      initialValues={atsIntegrationData}
      onSubmit={(values, helpers) =>
        submit(
          {
            is_active: true,
            report_url_type: values.report_url_type,
            unified_integration: {
              connection_id: unifiedConfig!.connection_id,
              default_package_id: values.default_package_id,
              trigger_stage: values.trigger_stage,
              customer_group_id: values.customer_group_id,
              type: integrationType,
            },
          },
          helpers
        )
      }
      validationSchema={validationSchema}
      validateOnMount
    >
      {({ handleSubmit, isSubmitting, isValid, setFieldValue, values }) => {
        return (
          <UnifiedIntegrationStepLayout
            onComplete={handleSubmit}
            disableCompletion={!isValid}
            submitting={isSubmitting}
          >
            <Container
              layout="narrow"
              testID="unified-details-page-outermost-container"
              className={styles.container}
            >
              <Stack gap="space-2">
                <div>
                  <Text style="input/section-title">
                    {t("step {{stepNumber}} of {{stepCount}}", {
                      stepNumber: 2,
                      stepCount: 2,
                    })}
                  </Text>
                </div>
                <Text
                  as="h1"
                  className={styles.titleSpacing}
                  style="global/title-small"
                >
                  {t("{{integration}} integration", {
                    integration: "Unified",
                  })}
                </Text>
                <ProgressLoader floating value={100} />
              </Stack>
              <Stack gap="space-4">
                <SectionMessage type="success" icon="verified_user">
                  {t(
                    "Unified has successfully authenticated with your account."
                  )}
                </SectionMessage>

                <SectionMessage type="info" icon="info">
                  <Trans>
                    For detailed instructions on setting up your Unified
                    integration, see our Unified{" "}
                    <Link textColor="text-weak" testID="deployment-guide">
                      Deployment Guide
                    </Link>
                    .
                  </Trans>
                </SectionMessage>

                <Text style="global/heading-medium">
                  {t(
                    "Next, select the package you want to configure for your Unified integration."
                  )}
                </Text>
              </Stack>
              <div className={styles.mainContentContainer}>
                <section>
                  <SelectFormikField
                    name="trigger_stage"
                    label={t("Trigger stage")}
                    placeholder={t("Please select")}
                    isRequired
                    isLoading={isLoadingTriggerStages}
                    testID="ats-integration-trigger-stage-select"
                  >
                    {triggerStages?.map(({ id, text }) => (
                      <SelectItem key={id} id={id}>
                        {text}
                      </SelectItem>
                    ))}
                  </SelectFormikField>
                </section>
                <section>
                  <SelectFormikField
                    label={t("Default package")}
                    testID="default-package"
                    name="default_package_id"
                    isRequired
                    isLoading={isLoadingPackages}
                  >
                    {packages?.map(({ id, name }) => (
                      <SelectItem key={id} id={id} textValue={name}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectFormikField>
                </section>
                <LinkButton
                  onPress={() => window.open("/packages/create/")}
                  testID="unified-create-package-link"
                >
                  {t("Create a new package")}
                </LinkButton>{" "}
                <section>
                  <SelectFormikField
                    label={t("Customer group")}
                    testID="customer-group"
                    name="customer_group_id"
                    isRequired
                    isLoading={isLoadingCustomerGroups}
                  >
                    {customerGroups?.map(({ id, name }) => (
                      <SelectItem key={id} id={id} textValue={name}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectFormikField>
                  <LinkButton
                    onPress={() => window.open("/groups/create/")}
                    testID="unified-create-groups-link"
                  >
                    {t("Create a new group")}
                  </LinkButton>
                </section>
                <Divider />
                <Text style="global/heading-medium">
                  {t("Advanced settings")}
                </Text>
                <section>
                  <RadioGroupFormikField
                    label={t("Report URL type")}
                    testID="report-url-type"
                    name="report_url_type"
                    orientation="horizontal"
                    isRequired
                    defaultValue="DIRECT_PDF_URL"
                  >
                    <Radio testID="direct-pdf-url" value="DIRECT_PDF_URL">
                      {t("PDF URL")}
                    </Radio>
                    <Radio testID="client-portal-url" value="CLIENT_PORTAL_URL">
                      {t("Client Portal URL (Authenticated)")}
                    </Radio>
                  </RadioGroupFormikField>
                </section>
              </div>
            </Container>
          </UnifiedIntegrationStepLayout>
        );
      }}
    </Formik>
  );
}

const styles = {
  titleSpacing: css({ marginBottom: "var(--space-1)" }),
  mainContentContainer: css({
    display: "flex",
    gap: "var(--space-5)",
    flexDirection: "column",
  }),
  container: css({
    rowGap: "1rem !important",
  }),
  textFieldSpacing: css({
    width: "440px !important",
  }),
};
